import { useMutation, useQuery } from "@apollo/client";
import { GET_OUTFITS_QUERY } from "@/graphql/queries";
import OutfitCard from "@/components/outfits/OutfitCard";
import Breadcrumb from "@/components/Breadcrumb";
import { useNavigate } from "react-router-dom";
import { useFavoriteOutfit } from "@/hooks/useFavoriteOutfit";
import OutfitsEmptyState from "@/components/empty-states/OutfitsEmptyState";
import { DELETE_OUTFIT_MUTATION } from "@/graphql/mutations/outfit";

export default function OutfitsPage() {
  // Fetch current user and outfits
  const { data, loading, error } = useQuery(GET_OUTFITS_QUERY);
  const outfits = data?.getCurrentUser?.outfits || [];
  const navigate = useNavigate();
  const { toggleFavorite } = useFavoriteOutfit();
  const [deleteOutfit, { loading: deleting }] = useMutation(DELETE_OUTFIT_MUTATION, {
    refetchQueries: [{ query: GET_OUTFITS_QUERY }],
    awaitRefetchQueries: true,
  });

  const handleFavoriteToggle = async (id: string) => {
    try {
      await toggleFavorite(id);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = window.confirm("Delete this outfit? This cannot be undone.");
    if (!ok) return;
    try {
      const { data } = await deleteOutfit({ variables: { id } });
      if (!data?.deleteOutfit?.success) {
        const msg = data?.deleteOutfit?.errors?.fullMessages?.join('\n') || 'Failed to delete outfit.';
        alert(msg);
      }
    } catch (e: any) {
      console.error('Delete outfit failed:', e);
      alert(e?.message || 'Failed to delete outfit.');
    }
  };

  return (
    <>
      <Breadcrumb
        title="Lookbook"
        trail={[{ label: "Dashboard", to: "/" }, { label: `Lookbook` }]}
      />
      {/* Outfits Grid */}
      <section className="mb-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-gray-500 dark:text-gray-400">
              Loading outfits...
            </div>
          ) : error ? (
            <div className="text-red-500">
              Error loading outfits. {error.message}
            </div>
          ) : outfits.length === 0 ? (
            <OutfitsEmptyState />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {outfits.map((outfit: any) => (
                <OutfitCard
                  key={outfit.id}
                  id={outfit.id}
                  occasion={outfit.occasion}
                  imageUrl={outfit.imageUrl}
                  outfitItems={outfit.outfitItems}
                  createdAt={outfit.createdAt}
                  updatedAt={outfit.updatedAt}
                  favorited={outfit.favorited}
                  onClick={() => navigate(`/lookbook/${outfit.id}`)}
                  onFavoriteToggle={handleFavoriteToggle}
                  onDelete={deleting ? undefined : handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
